import { Backdrop, Box, Button, Card, CardHeader, CircularProgress, Divider, FormControl, Link, MenuItem, Select, Stack, 
  useTheme, Tooltip, Typography, alpha } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { ListContextProvider, useCreateContext, useGetMainList, useListContext, useListController, useRefresh, useShowContext } from "ra-core";
import { FC, useMemo } from "react";
import { RecPatch } from "~/dataProvider/tools";
import { ObfuscationRuleDto, FieldObfsMappingDto, ObfuscationDataType, ObfuscationRuleType, EditObfuscationMappingInput, 
  ObfuscationMappingResponseDto, ObfuscationRuleStatus, DatabaseType } from "~/gen/api/encrypt";
import { ExtraIgnoreItem, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { FunctionField } from "../../field";
import { Datagrid } from "../../list/datagrid";
import Scrollbar from "../../Scrollbar";
import { dataTypeDisplayInfo, decideDataTypeMaskingKind, figureOutNumericSubKind, getNumericTypeStorageLength, 
  isSinglePrecisionFloatPoint } from "../../governance/fieldEdit/maskingRuleDataTypesMap";
import Create from "~/controller/Create";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { useEditObfsMappingContext } from "./EditObfsMappingContext";
import { displayInferredType, figureOutDateRank, merge_ora_p_s } from "./obfsRuleCompatibleMapping";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import { disabledColor } from "~/utils/a11y";
import { useBigTableMode } from "./FieldsMappingList.hooks";


const ObfsFieldsMappingList: FC = () => {
  const { currTable } = useEditObfsMappingContext()
  const handlers = useDefaultSavedHandlers("Obfuscation Mappings Update")

  const fieldListCtrl = useListController({ 
    resource: currTable ? 'obfs-field' : 'noop',
    basePath: '',
    filter: { databaseId: currTable?.databaseId, tableId: currTable?.tableId }
  })
  
  return (
    <ListContextProvider value={fieldListCtrl}>
      <Create resource="encryption/obfs/mappings" basePath="" {...handlers} >
        <ObfsFieldsMappingListBody />
      </Create>
    </ListContextProvider>
  )
}


const ObfsFieldsMappingListBody: FC = () => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const { record } = useShowContext<ObfuscationMappingResponseDto & RecPatch>()
  const { databaseId, currTable, setFieldObfsRul, clearMappings, clearFieldMapping, mappingEdits } = useEditObfsMappingContext()
  const listCtx = useListContext<FieldObfsMappingDto & RecPatch>()

  const { data: obfsRulesData } = useGetMainList<ObfuscationRuleDto & RecPatch>(
    databaseId ? 'settings/encryption/obfuscation-rule' : 'noop',
    pages(), sorts(), {}, { action: 'EDIT_OBFS_MAPPING' }
  )

  const { data: classificationData } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>(
    'settings/governance/data-classification', 
    pages(), sorts(), {}, { action: 'EDIT_OBFS_MAPPING' }
  )

  const {
    bigTableModeOn,
    SearchInput,
    ListBox,
    popoutOpen,
    editorOnlyListCtx,
    filteredItemCount,
    isStagedField,
    removeFieldFromEditor,
    resetStagedFields,
  } = useBigTableMode({isFieldMapped, clearFieldMapping,
    isRowSelectable: () => false, bulkEditMappings: () => {},
  })
  
  const { getObfsRuleFromCache } = useMemo(() => {
    const cacheB = {} as any
    const obfsRules = Object.values(obfsRulesData ?? {}).sort((a, b) => a.id - b.id)

    const getObfsRuleFromCache_inner = (field: FieldObfsMappingDto): 'n/a' | {rules: ObfuscationRuleDto[], warns: number[]} => {
      const f_dt = (field.dataType ?? '').toLowerCase()
      const f_maskingKind = decideDataTypeMaskingKind(f_dt)
      if (f_maskingKind === 'time' || f_maskingKind === 'not_support' || f_dt === 'year') { return 'n/a' }
  
      const cacheKey = `${field.dataType}-${field.maxLength}-${field.precision}-${field.scale}`
      let cacheEntry : {rules: ObfuscationRuleDto[], warns: number[]} = cacheB[cacheKey]
      if (!cacheEntry) {
        const f_obfsDataType = f_maskingKind === 'date' ? ObfuscationDataType.DateTime
          : f_maskingKind === 'integer_num' ? ObfuscationDataType.Integer
          : f_maskingKind === 'non_integer_num' ? ObfuscationDataType.Numeric
          : ObfuscationDataType.String
        const numSubKind = figureOutNumericSubKind(f_dt)
        const warns: number[] = []
        
        const rules: ObfuscationRuleDto[] = obfsRules.filter(r => {
          if (r.ruleType !== undefined && +r.ruleType === ObfuscationRuleType.ClearData) {
            // ClearData rule applys to all data types, no range check required, 
            // but check for field not being pk or fk and being nullable, on per field bases
            return true
          }
          const rIsTableColumn = r.ruleType !== undefined && +r.ruleType === ObfuscationRuleType.TableColumn
          if (rIsTableColumn && r.databaseId !== databaseId) { return false } // database shall match, for tableColumn rule
          if (!r.inferredType || r.dataType !== f_obfsDataType) { return false } // dataType shall be compatible
          if (r.status === ObfuscationRuleStatus.Obsolete) { return false }
  
          // data range check
          switch (f_obfsDataType) {
            case ObfuscationDataType.Integer: {
              const fBytes = (field as any).weird ? (field.maxLength ?? 0) 
                : getNumericTypeStorageLength({ ...field, type: f_dt }) ?? 0
              const rBytes = r.inferredType?.length ?? 100
              if (rIsTableColumn) {
                if (fBytes < rBytes) { warns.push(r.id) }
                return true
              } else {
                return fBytes >= rBytes
              }
            }
            case ObfuscationDataType.String: {
              const f_factor = f_dt.startsWith('n') ? 2 : 1
              const fLen = Math.floor(
                (['text', 'ntext', 'mediumtext', 'longtext'].indexOf(f_dt) > -1 && record?.databaseType !== DatabaseType.Snowflake
                  ? Infinity 
                  : (field.maxLength ?? 0)) 
                / f_factor)
              const rLen = r.inferredType?.length ?? Number.MAX_SAFE_INTEGER
              if (rIsTableColumn) {
                const r_factor = (r.inferredType.sourceColumnType || '').toLocaleLowerCase().startsWith('n') ? 2 : 1
                if (fLen < Math.floor(rLen / r_factor)) { warns.push(r.id) }
                return true
              } else {
                return fLen >= rLen
              }
            }
            case ObfuscationDataType.DateTime: { 
              const f_rnk = figureOutDateRank(f_dt)
              const inRange = f_rnk >= (r.inferredType.length ?? 3)
              if (rIsTableColumn) {
                if (!inRange) { warns.push(r.id) }
                return true
              } else {
                return inRange
              }
            }
  
            case ObfuscationDataType.Numeric: {
              if (!numSubKind) { return false }
              const rSubKind = figureOutNumericSubKind(r.inferredType?.sourceColumnType ?? '')
              switch (numSubKind) {
                case 'fix_point_num': {
                  const isOra = f_dt === 'number'
                  const f_p = field.precision ?? 
                    (f_dt === 'decfloat' 
                      ? 34  // db2 decfloat          p: 34 
                      : 18) // mssql decimal default p: 18
                  const f_s = field.scale ?? 0
                  const r_p = (isOra ? r.inferredType.pOra : r.inferredType.p) ?? Infinity
                  const [r_s] = [(isOra ? r.inferredType.sOra : r.inferredType.s)].map(x => x ? x : x === 0 ? 0 : Infinity)
                  if (isOra && !field.precision && !field.scale) {
                    // oracle number with no precision and no scale specified, it's effectively a range [38, -84]~[38, 127]
                    return r_p > 0 && r_p <= 38 && r_s <= 127 && r_p - r_s <= 126
                  }
                  const {pOra:p_x, sOra:s_x} = merge_ora_p_s({ pOra: f_p, sOra: f_s }, { pOra: r_p, sOra: r_s })
                  
                  // for db2 decfloat, only compare precision will suffice
                  if (f_dt === 'decfloat') { return f_p >= r_p }
                  
                  if (rIsTableColumn) {
                    if (rSubKind !== numSubKind) { return false }
  
                    if (isOra 
                      ? (p_x !== f_p || s_x !== f_s) 
                      : (r_p > f_p || r_s > f_s)) { warns.push(r.id) }
                    return true
                  } else {
                    return isOra 
                      ? p_x === f_p && s_x === f_s 
                      : r_p <= f_p && r_s <= f_s
                  }
                }
                case 'float_point_num': {
                  const f_pn = 
                    isSinglePrecisionFloatPoint(f_dt) ? 24
                    : (f_dt === 'float' && record?.databaseType !== DatabaseType.Snowflake) ? (field.precision ?? 0) 
                    : 53
                  if (rIsTableColumn) {
                    if (rSubKind !== numSubKind) { return false }
                    const r_dt = (r.inferredType.sourceColumnType ?? '').toLocaleLowerCase()
                    const r_pn = 
                      isSinglePrecisionFloatPoint(r_dt) ? 24
                      : r_dt === 'float' ? (r.inferredType.pn ?? Infinity)
                      : 53
  
                    if (f_pn < r_pn) { warns.push(r.id) }
                    return true
                  } else {
                    return f_pn >= (r.inferredType.pn ?? Infinity)
                  }
                }
                case 'money': {
  
                  if (rIsTableColumn) {
                    if (rSubKind !== numSubKind) { return false }
                    const r_dt = (r.inferredType.sourceColumnType ?? '').toLocaleLowerCase()
                    if (f_dt === 'smallmoney' && r_dt !== 'smallmoney') { warns.push(r.id) }
                    return true
                  } else {
                    const f_p = f_dt === 'smallmoney' ? 6 : 15
                    const r_p = r.inferredType.p ?? Infinity
                    const r_s = r.inferredType.s ?? Infinity
                    return f_p >= (r_p - r_s) && 4 >= r_s
                  }
                }
              }
            }
            default: { return false }
          }
        })
  
        if (f_obfsDataType === ObfuscationDataType.Numeric 
        && f_dt === 'number' 
        && (field.scale || 0) === 0) { // oracle number with scale 0, effective an integer type
          // thus we shall extend the selection that allow integer valueList/valueRange
          const f_p = field.precision ?? 0 // number of decimal digits
          // "maxLength": the max bytes count for a safe integer to be stored into the field
          const maxLength = Math.floor(
            Math.ceil(f_p * 3.32193) // magic num (3.32193), decimal digits to binary digits approximately
            / 8)
  
          const fieldRepresent = { dataType: 'int', maxLength, weird: true }
          const extended = getObfsRuleFromCache_inner(fieldRepresent)
          if (extended !== 'n/a') {
            rules.push(...(
              extended.rules.filter(r => rules.indexOf(r) < 0) // remove duplicates, then add
            ))
          }
        }
        cacheEntry = { rules, warns }
        cacheB[cacheKey] = cacheEntry
      }
  
      // ClearData rule applys to all data types, no range check required, but the field should not be pk or fk and be nullable
      if (field.isPrimaryKey || field.isForeignKey || !field.isNullable) {
        return { ...cacheEntry, rules: cacheEntry.rules.filter(r => r.ruleType !== ObfuscationRuleType.ClearData)}
      }
  
      return cacheEntry
    }
  
    return {getObfsRuleFromCache: getObfsRuleFromCache_inner}
  }, [databaseId, obfsRulesData, record?.databaseType])


  const synthObfsRuleValue = (x: FieldObfsMappingDto) => {
    const ruleId = mappingEdits[x.fieldId ?? -1]
    if (ruleId === 'rm_map') { return '' }
    else if (ruleId) { return (obfsRulesData || {})[ruleId] }
    else {
      return (obfsRulesData || {})[x.obfuscationRuleId || -1] || ''
    }
  }

  const refresh = useRefresh()
  const { saving } = useCreateContext()
  const save = useSimpleSaveAction()

  const mappingEditsCount = Object.keys(mappingEdits).length
  
  const saveMappings = () => {
    if (!currTable) { return }
    if (mappingEditsCount === 0) { return }

    const obfsMappings = Object.keys(mappingEdits).map((k) => {
      const fieldId = +k
      const mapping = mappingEdits[+k]
      return { fieldId, removeMapping: mapping === 'rm_map', obfuscationRuleId: mapping === 'rm_map' ? null : mapping }
    })
    const fieldData = listCtx.data || {}
    const updatedFields = Object.keys(mappingEdits).map(k => {
      const field = fieldData[`${databaseId}-${currTable?.tableId}-${k}`]
      const mapping = mappingEdits[+k]
      return { ...field, obfuscationRuleId: mapping === 'rm_map' ? null : mapping }
    })

    const toSave: EditObfuscationMappingInput = {
      databaseId,
      tableId: currTable?.tableId || 0,
      obfsMappings,
    }
    resetStagedFields()
    save({toSave, updatedFields}, {
      onSuccess: () => {
        clearMappings()
        refresh()
      }
    })
  }

  const theme = useTheme()
  const rowStyles = (d:FieldObfsMappingDto&RecPatch) => {
    if (!bigTableModeOn || !isStagedField(d.fieldId!)) { return undefined }
    
    return { backgroundColor: alpha(theme.palette.success.light, 0.05) };
  }

  return (
    <Card>
      <CardHeader 
        title={
          !bigTableModeOn ? "Mappings" :
          <Stack component="span" direction="row" spacing={4} sx={{ mr: 'auto' }} alignItems="center">
            <Typography variant="h6">Mappings</Typography>
            <Box component="span" sx={{ border: '1px solid InactiveCaptionText', borderRadius: 0.75, p: 0.5, transform: 'scale(0.7)', lineHeight: '12px' }}>
              <Typography variant="caption" color="InactiveCaptionText">big table mode</Typography>
            </Box>
            {SearchInput}
            {filteredItemCount > 0 ? <Typography variant="caption" color="info.light" sx={{ml:'12px !important'}}>{filteredItemCount}</Typography> : null}
          </Stack>
        } 
        action={
          hasWritePermit && mappingEditsCount > 0 ?

          <Stack direction="row" spacing={2}>
            <Button
              disabled={saving}
              variant="outlined"
              onClick={clearMappings}
            >
              Cancel
            </Button>
            <Button
              disabled={saving}
              variant="contained"
              onClick={() => saveMappings()}
            >
              Save
            </Button>
          </Stack> :
          null
          // <Tooltip title="Learn about Encryption Rules">
          //   <IconButton
          //     component={RouteLink}
          //     to="/documents/how-to_guides/encryption/encryption_rules"
          //     color={"secondary" as any}
          //   >
          //     <HelpOutlineIcon />
          //   </IconButton>
          // </Tooltip>
        }
      />
      {bigTableModeOn && popoutOpen &&
        <>
          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" sx={{ mx: 2 }}>
              <Typography variant="subtitle2" sx={{ mr: 'auto' }}>Choose:</Typography>
            </Stack>
            {ListBox}
          </Box>
        </>}
      <Divider />
      <Scrollbar>
        <Box sx={{ minWidth: 1150, position: 'relative' }}>
          <Datagrid 
            hasBulkActions={false}
            sx={{ 
              '& th' : { py: 2, '& span': { display: 'inline-block' } },
              '& td' : { py: 2 },
              ...(!hasWritePermit && {
                '& div.Mui-disabled': disabledColor,
              })
            }}
            empty={<Typography variant="subtitle1" textAlign="center" color="InactiveCaptionText" sx={{ my: 1 }}>Empty Mapping</Typography>}
            rowStyle={rowStyles}
            {...(!bigTableModeOn ? listCtx : editorOnlyListCtx)}
          >
            <FunctionField 
              source="fieldName" 
              label={<Box component="span" sx={{ minWidth: 72 }}>Field Name</Box>} 
              sortable={false} 
              render={(d?: FieldObfsMappingDto & RecPatch) => {
                if (!d) { return null}
                if (!isStagedField(d.fieldId!)) { return (<Typography component="span" variant="body2">{d.fieldName}</Typography>)}
                return (
                  <Tooltip title="Remove from editor">
                    <Button
                      onClick={() => removeFieldFromEditor(d.fieldId!)}
                      startIcon={<ClearIcon />}
                      sx={{
                        '& .MuiSvgIcon-root': { opacity: 0, },
                        '&:hover .MuiSvgIcon-root, &:focus .MuiSvgIcon-root': { opacity: 1, },
                      }}
                    >
                      {d.fieldName}
                    </Button>
                  </Tooltip>
                )
              }}
            />
            <FunctionField 
              source="dataType" 
              label={<Box component="span" sx={{ minWidth: 64 }}>Data Type</Box>}
              sortable={false} 
              render={(d?: FieldObfsMappingDto & RecPatch) => {
                if (!d) { return null }
                return (
                  <Typography component="span" variant="body2"
                    sx={{
                      '& div': { transform: 'scale(0.8) translateX(-12px)', },
                      '& span.MuiTypography-caption': { mr: 0.5, },
                    }}
                  >
                    {(d.isPrimaryKey || d.isForeignKey || d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) && <Box>
                      {d.isPrimaryKey && <Typography variant="caption" color="primary">[PK]</Typography>}
                      {d.isForeignKey && <Typography variant="caption" color="secondary">[FK]</Typography>}
                      {d.isIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity]</Typography>}
                      {d.isComputed && <Typography variant="caption" color="InactiveCaptionText">[Computed]</Typography>}
                      {d.extraIgnoreItem === ExtraIgnoreItem.TransitiveIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity - transitive]</Typography>}
                      <br />
                    </Box>}
                    {dataTypeDisplay(d, record?.databaseType)}
                  </Typography> 
                )
              }}
            />
            <FunctionField 
              label={<Box component="span" sx={{ width: 200, display: 'inline-block' }}>Classification</Box>}
              sortable={false}
              render={(d?: FieldObfsMappingDto & RecPatch) => {
                if (!d) { return null}
                const cls = (classificationData || {})[d.secondaryClassificationId || -1]
                
                return (
                  !cls ? null :
                  <Typography variant="body2">
                    {cls.primaryClassificationText} - {cls.name}
                  </Typography>
                )
              }}
            />
            <FunctionField 
              source="Obfuscation Rule" 
              label={<Box component="span" sx={{ width: 200, display: 'inline-block' }}>Obfuscation Rule</Box>} 
              sortable={false} 
              render={(d?: FieldObfsMappingDto & RecPatch) => {
                if (!d) { return null }
                const isId = d.isIdentity || d.extraIgnoreItem === ExtraIgnoreItem.TransitiveIdentity
                if (d.isComputed || d.extraIgnoreItem === ExtraIgnoreItem.RowVersionGeneratedAlways 
                || (isId && !d.isForeignKey) // #!! it's legitimate for FK of identity to have TableColumn obfs rule !
                || ('timestamp' === d.dataType?.toLowerCase() 
                  && record?.engineVersion && record.engineVersion.toLowerCase().indexOf('sql server') > 0)
                ) {
                  return (
                    <Typography variant="body2">N/A</Typography>
                  )
                }
                
                const obfsRule = synthObfsRuleValue(d)
                const cacheEntry = getObfsRuleFromCache(d)
                return (
                  cacheEntry === 'n/a' ? 'N/A' :
                  <FormControl>
                    <Select
                      value={obfsRule}
                      disabled={!hasWritePermit}
                      onChange={(e) => {
                        const val = e.target.value as ObfuscationRuleDto | ''
                        setFieldObfsRul(d, val || 'rm_map')
                      }}
                      size="small"
                      sx={{ minWidth: 80 }}
                    >
                      <MenuItem value={""}>
                        <Typography variant="caption" color="InactiveCaptionText">[None]</Typography>
                      </MenuItem>
                      {obfsRule && cacheEntry.rules.every(r => r.ruleId !== obfsRule.id) &&
                        <MenuItem value={obfsRule as any}>
                          <Typography variant="body2" color="error.main">{obfsRule.ruleName}</Typography>
                        </MenuItem>}
                      {(isId && d.isForeignKey 
                        ? cacheEntry.rules.filter(r => r.ruleType === ObfuscationRuleType.TableColumn) 
                        : cacheEntry.rules
                        ).map(r => (
                        <MenuItem key={r.ruleId} value={r as any}>{r.ruleName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              }}
            />
            <FunctionField 
              label={<Box component="span" sx={{ width: 200, display: 'inline-block' }}> </Box>} 
              sortable={false} 
              render={(d?: FieldObfsMappingDto & RecPatch) => {
                if (!d) { return null }
                const obfsRule = synthObfsRuleValue(d)
                if (!obfsRule) { return null }
                const cacheEntry = getObfsRuleFromCache(d)
                if (cacheEntry === 'n/a') { return null }
                return (
                  <>
                    {obfsRule.ruleType === ObfuscationRuleType.TableColumn && 
                      <>
                      <Typography>Table Column</Typography>
                      {obfsRule.status === ObfuscationRuleStatus.Obsolete &&
                        <Typography component="p" variant="caption" color="warning.main" sx={{ maxWidth: 300 }}>
                          This rule is obsolete due to database schema change, it will take no effect in actual obfuscation process.</Typography>}
                      {obfsRule.inferredType?.sourceColumnName && 
                        <Typography component="p" variant="caption">Source Column Name: <b>{obfsRule.inferredType?.sourceColumnName}</b></Typography>}
                      {obfsRule.inferredType?.sourceColumnType &&
                        <Typography variant="caption">Source Column Type: <b>{
                          dataTypeDisplay({ 
                            maxLength: obfsRule.inferredType.length, 
                            precision: obfsRule.inferredType.p,
                            scale: obfsRule.inferredType.s,
                            dataType: obfsRule.inferredType.sourceColumnType,
                          }, record?.databaseType)
                        }</b></Typography>}
                      </>
                    }
                    {obfsRule.ruleType !== ObfuscationRuleType.TableColumn &&
                      <>
                      <Typography>
                        { obfsRule.ruleType === ObfuscationRuleType.ValueList  ? 'Value List' 
                        : obfsRule.ruleType === ObfuscationRuleType.ValueRange ? 'Value Range' 
                        : obfsRule.ruleType === ObfuscationRuleType.ClearData  ? 'Clear Data' 
                        : null}
                      </Typography>
                      {obfsRule.ruleType !== ObfuscationRuleType.ClearData &&
                        displayInferredType(obfsRule.inferredType as any)}
                      {obfsRule && cacheEntry.rules.every(r => r.ruleId !== obfsRule.id) &&
                        <Typography variant="caption" color="error.main" component="p">
                          This rule's data range exceeds the mapped field. You should consider change to another rule.
                        </Typography>
                      }
                      </>
                    }
                    {obfsRule.description &&
                      <Tooltip title={obfsRule.description}>
                        <Link><Typography component="p" variant="caption">Read Description</Typography></Link>
                      </Tooltip>
                    }
                    {cacheEntry.warns.indexOf(obfsRule.id) > -1 && 
                      <Typography component="p" variant="caption">
                        <Typography color="warning.main" variant="caption">Be careful! </Typography>
                        The data source has bigger range data type than the mapped field.
                      </Typography>}
                    {/* <Typography>More about the rule...</Typography> */}
                  </>
                )
              }}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      <Backdrop open={listCtx.loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    </Card>
  )
}


const isFieldMapped = (x:FieldObfsMappingDto) => x.obfuscationRuleId != null

const dataTypeDisplay = (d:FieldObfsMappingDto, dbType?:DatabaseType) => {
  const info = dataTypeDisplayInfo(d, dbType)
  return (info.dataType || '') + (info.dataTypeExt || '')
}

export default ObfsFieldsMappingList;
